import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Router, ResolveStart } from "@angular/router";
import { filter, map, distinctUntilChanged } from "rxjs/operators";
import { RouteData } from "./shared/interfaces/routingParametrs";
import { Title } from "@angular/platform-browser";
import { CommunicationService } from "./shared/services/communication/communication.service";
import { Company } from "./shared/interfaces/company";
import { CommonHttpService } from "./shared/services/common-http/common-http.service";
import { ErrorHandlerService } from "./shared/services/error-handler/error-handler.service";
import { Subscription } from "rxjs";
import { VpcCommsService } from "./shared/services/vpc-comms/vpc-comms.service";
import { profileRoles } from "./shared/interfaces/sessionStorageModel";
import { SessionStorageService } from "./shared/services/session-storage/session-storage.service";
import { SendIframeUrlService } from "./shared/services/send-iframe-url/send-iframe-url.service";
import { AuthService } from "./core/authentication/auth.service";
import { GoogleAnalyticsService } from "./shared/services/google-analytics/google-analytics.service";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  routeData: RouteData;
  isOnlyTopNavigation: boolean;
  isBothNavigation: boolean;
  isNoNavigation: boolean;
  isOffline = false;
  selectedModule: string;
  companyId: string;
  subscription: Subscription;
  subscriptionCss: Subscription;
  loggedInUserRole: string;
  isMenuCollapsed = false;
  companyCustomCss = `<style> </style>`;
  constructor(
    private router: Router,
    private titleService: Title,
    private sessionStorageService: SessionStorageService,
    private communicationService: CommunicationService,
    private commonServices: CommonHttpService,
    private vpcService: VpcCommsService,
    private errorHandlerService: ErrorHandlerService,
    private sendIframeUrlService: SendIframeUrlService,
    private authService: AuthService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.titleService.setTitle("vPlaybook");
    this.subscribeNavigation();
  }

  @HostListener("window:popstate", ["$event"])
  onPopState(event) {
    const constCompanyData = this.sessionStorageService.getCompanyData();
    if (constCompanyData) {
      this.communicationService.sendCompany(constCompanyData);
    }
  }

  ngOnInit() {
    if (this.vpcService.isWebView() || this.vpcService.CheckNodeWebkit()) {
      this.isOffline = true;
    }
    this.subScribeRouteForCompanyId();

    setInterval(() => {
      this.authService.manager
        .signinSilent()
        .then(() => this.authService.reloadUser());
    }, 5 * 60 * 1000);
  }

  getCompanyDetails(companyId: number) {
    this.commonServices
      .get<Company>({
        uri: "/api/Companies/" + companyId,
      })
      .subscribe(
        (response) => {
          // Defer to a microtask so the assignment lands in a fresh CD cycle.
          // Otherwise this can fire synchronously during the initial CD pass
          // and trigger NG0100 ExpressionChangedAfterItHasBeenCheckedError on
          // the [innerHTML] binding in app.component.html.
          Promise.resolve().then(() => {
            const css = response && response.consoleColorCSS ? response.consoleColorCSS : '';
            this.companyCustomCss = `<style> ${css} </style>`;
          });
          this.communicationService.sendCompany(response);
        },
        (error) => {
          this.communicationService.sendCompany(null);
          this.errorHandlerService.handleError(error);
        }
      );
  }

  isTopSpacer185() {
    const moduleArray = [
      "Playbooks",
      "People",
      "Roles",
      "Help",
      "BulkAction",
      "Tags",
      "CustomFields",
      "Notification",
      "PermissionsGroups",
      "Report",
      "scheduledReport",
      "ReportDescription",
      "AdminDashboard",
      "AdminChallenges",
      "AdminLeaderboard",
      "AdminFeaturedContent",
      "ChallengeResponses",
      "challengetemplate",
      "CoachingDashboard",
      "Challenges",
      "CoachingLeaderboard",
      "FeaturedContent",
      "ChallengesTemplate",
      "TeamAssessment",
      "ChallengeFeedback",
      "RepCoachingDashboard",
      "RepChallenges",
      "RepLeaderboard",
      "RepFeaturedContent",
      "RepFeedback",
      "AdminLmsDashboard",
      "AdminLmsLeaderboard",
      "AdminLmsViewTemplate",
      "AdminLmsEditTemplate",
      "AdminLmsParticipantBreakdown",
      "ManagerLmsDashboard",
      "ManagerLmsLeaderboard",
      "ManagerLmsViewTemplate",
      "ManagerLmsEditTemplate",
      "ManagerLmsParticipantBreakdown",
      "UserLmsDashboard",
      "UserLmsLeaderboard",
      "UserLmsViewTemplate",
      "HierarchyMap",
    ];

    return moduleArray.indexOf(this.selectedModule) !== -1;
  }

  subScribeRouteForCompanyId() {
    this.subscription = this.communicationService
      .getCompanyId()
      .pipe(distinctUntilChanged())
      .subscribe((companyId) => {
        try {
          if (companyId) {
            // Check if the previous company and current company is same in that
            // case we will not get server call for company details.
            this.getCompanyDetails(companyId);
          } else {
            this.communicationService.sendCompany(null);
          }
        } catch (e) {
          this.communicationService.sendCompany(null);
          // To Do: Redirect to Error page.
        }
      });
    this.subscriptionCss = this.communicationService
      .getCompany()
      .subscribe((company) => {
        // Defer to a microtask so the assignment lands in a fresh CD cycle.
        // This subscription can emit synchronously during the initial CD pass
        // (BehaviorSubject-style), and writing companyCustomCss in the same
        // CD cycle that just rendered it triggers NG0100. Also: previously
        // the falsy branch interpolated the literal string "null" into the
        // <style> tag — now it falls back to '' so the DOM stays clean.
        Promise.resolve().then(() => {
          const css = company && company.consoleColorCSS ? company.consoleColorCSS : '';
          this.companyCustomCss = `<style> ${css} </style>`;
        });
      });
  }

  // Subscribe the navigation change for get custom data from the route.
  subscribeNavigation() {
    this.router.events
      .pipe(
        filter((event) => event instanceof ResolveStart),
        map((event: any) => {
          let data = null;
          let route = event["state"].root;
          while (route) {
            data = route.data || data;
            route = route.firstChild;
          }
          this.sendIframeUrlService.sendUrlToParent(event.url);
          return data;
        })
      )
      .subscribe((result) => {
        this.routeData = result;
        if (this.routeData) {
          this.titleService.setTitle(this.routeData.title);
          this.selectedModule = this.routeData.selectedModule;
        }
        // Set the navigation.
        this.setNavigation();
      });
  }

  // Set navigation.
  // This function will decide which navigation needs to display.
  setNavigation() {
    this.isOnlyTopNavigation = false;
    this.isBothNavigation = false;
    this.isNoNavigation = true;

    try {
      this.loggedInUserRole =
        this.sessionStorageService.getLoggedInProfileDetail().role;
    } catch {}

    if (this.routeData) {
      if (this.routeData.topNavigation && !this.routeData.sideNavigation) {
        this.isNoNavigation = false;
        this.isOnlyTopNavigation = true;
      } else if (
        this.routeData.topNavigation &&
        this.routeData.sideNavigation
      ) {
        this.isNoNavigation = false;
        this.isBothNavigation = true;
        if (this.selectedModule === "profile") {
          if (
            [
              profileRoles.administrator.toString(),
              profileRoles.playbookOwner.toString(),
              profileRoles.salesManager.toString(),
              profileRoles.user.toString(),
            ].indexOf(this.loggedInUserRole) !== -1
          ) {
            this.isBothNavigation = true;
          } else {
            this.isOnlyTopNavigation = true;
            this.isBothNavigation = false;
          }
        }
      } else if (
        !this.routeData.topNavigation &&
        !this.routeData.sideNavigation
      ) {
        this.isNoNavigation = true;
      } else {
        this.isNoNavigation = true;
      }
    }
  }
  changeToggleMenuStatus(status: boolean) {
    this.isMenuCollapsed = status;
  }
  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.subscriptionCss) {
      this.subscriptionCss.unsubscribe();
    }
  }

  goToBeta() {
    const profileDetails =
      this.sessionStorageService.getLoggedInProfileDetail();

    const companyId = profileDetails.vPlaybookCompanyAccess
      ? profileDetails.vPlaybookCompanyAccess
      : 1;
    // NOTE: In production, a company with Id 1 exists and is Active.
    // In the BE, there is logic which checks whether company exists and if it's active.
    // Currently, only Super admins can use this feature.
    // Later depending on the user role it goes through (If it's super admin, it doesn't check company access)
    // TODO: In the BE, adjust the logic for LoginAsProfile to skip certain checks (including validator) to allow null companyId
    // Another note:
    // This approach with hard-coded companyId was taken because it's a temporary feature.

    this.commonServices
      .post<ILoginAsProfileViewRequest, ILoginAsProfileViewResponse>({
        uri: `/api/Profiles/LoginAsProfile?profileId=${profileDetails.id}&companyId=${companyId}`,
      })
      .subscribe((response) => {
        window.open(
          `https://beta.vplaybook.com/?authCode=${response.authCode}`,
          "_blank"
        );
      });
  }
}

export interface ILoginAsProfileViewRequest {
  profileId: number;
  companyId: number;
}
export interface ILoginAsProfileViewResponse {
  id: number;
  profileId: number;
  adminId: number;
  companyId: number;
  authCode: string;
  logOffCode: string;
  isConsumed: boolean;
  isAuthorized: boolean;
  userName: string;
  firstName: string;
}
